// How a DATE reaches the eye in the lab page's result table.
//
// The engine's value arrives as a full ISO INSTANT and the table printed it verbatim —
// `cellText` is `String(value)` and `toIso` is `.toISOString()` (packages/engine/toolHandlers/
// findExecutionsByFilter.js), so a Created column read `2026-08-22T10:11:12.000Z`. That is the
// wire format on a screen, and the `Z` makes it worse than a bare day.
//
// ★ IT IS FORMATTED HERE, AT DISPLAY, AND THAT IS NOT ARBITRARY. The engine's value is SHARED
//   WITH THE CSV — export/exportShape.js is one source of truth for the preview rows AND the
//   downloaded file — so formatting it upstream would put "Aug 22, 2026" inside a download.
//   `cellText` mirrors the CSV serializer and must keep doing so.
//
// ★ A TIME APPEARS ONLY WHERE THE FIELD IS A DATE-AND-TIME. The decision is per FIELD, keyed on
//   the COLUMN, never on the value: an incident's occurrence is a moment by design and keeps its
//   clock; a due date is a day and must not grow one. The split is the product's own component
//   pair (CustomDateComp vs CustomDateAndTimeComp) and it is keyed on the engine's own
//   twelve-column `field` enum (packages/engine/tools.js) — the same source the product's AI
//   page uses, so the two pages cannot classify a column differently.
//
// ★ AND THE DAY ARM HAS TWO HALVES, which is the part a straight port gets wrong. A due /
//   closed / completed / assessment date is a CALENDAR DAY stored at midnight UTC — read in a
//   negative-offset zone it renders as the day before (measured: `Aug 21` under
//   America/New_York), so it is read in UTC. A created / modified date is a real timestamp that
//   the product happens to show as a day, so it is read in the viewer's zone.
//
// ★ THE MONTH NAME IS AN ENGLISH LITERAL, and it is a divergence from the product worth stating
//   rather than letting drift. The product renders through `datePickerReadableFormat`
//   (safetyqube_webapp/src/utils/helpers.js), which needs `moment` and `_tDate`'s i18n `common`
//   namespace; this client has `dayjs` and no i18n at all. The whole AI section here is English
//   literals by standing decision, so an English month is consistent with its neighbours — but
//   it is a literal, not a translation, and a future i18n pass owns it.
//
// 24-hour, which is the product's majority (28 sites against the permit family's 8) and what
// its own AI page chose. A column this cannot classify is left exactly as it was.

// `dayjs/plugin/utc.js`, WITH the extension. The conventional extensionless form compiles fine
// under the CRA build and is UNLOADABLE under node's ESM resolver, which is what the offline gate
// runs on — a plugin import that works in the app and not in the check is how a gate goes silent.
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc.js";
import { inZone } from "./messageTime.mjs";

dayjs.extend(utcPlugin);

const CALENDAR_DAY_FIELDS = new Set(["duedate", "closedon", "completedon", "assessmenton"]);
const TIMESTAMP_DAY_FIELDS = new Set(["createdon", "modifiedon"]);
// `dateOccurred` is the LISTING's key for the incident occurrence — not a camelCase of the enum's name, so it is listed on its
// own. The engine sends it as the true instant (the record's zone applied, RESUME ZR2-B), so it reads in the viewer's zone like
// every moment here, which is the clock the model's own copy states. Before this the lab printed the raw `…Z` stamp (P3A-B).
// A permit's `permitFrom` / `permitTo` are listing moment keys of the same kind (export/exportShape.js `_MOMENT`).
//
// ★ "THE VIEWER'S ZONE" IS THE PAGE'S `userTz` SETTING — the zone the engine is handed and writes its prose in
//   (loop.js localiseListingStamps) — not the browser's clock. Read on the browser's clock, the column disagreed with
//   the sentence above it whenever the two differ (a reader set to London in a browser in India). The conversion is
//   messageTime's `inZone`, the one the message times use. A blank setting is "Not set (UTC)", the zone the engine
//   counts it in, so it reads UTC here too and never the browser's clock.
const MOMENT_FIELDS = new Set(["incidentoccurrence", "dateoccurred", "raisedon", "issuedon", "activeon", "closureon", "expiredon", "permitfrom", "permitto"]);

// `createdOn` (the listing key) and `created_on` (a raw SQL alias) are the same column.
const normalise = (key) => String(key == null ? "" : key).replace(/[^a-z0-9]/gi, "").toLowerCase();

export function dateFieldKind(columnKey) {
  const k = normalise(columnKey);
  if (CALENDAR_DAY_FIELDS.has(k)) return "date";
  if (TIMESTAMP_DAY_FIELDS.has(k)) return "local-date";
  if (MOMENT_FIELDS.has(k)) return "datetime";
  return null;
}

// `new Date(value)` rather than a strict parse: the engine sends ISO, and anything that is not a
// date returns null so the caller prints what it was given unchanged.
function asMs(value) {
  if (value == null || value === "") return null;
  if (typeof value !== "string" && !(value instanceof Date) && typeof value !== "number") return null;
  const ms = new Date(value).getTime();
  return isNaN(ms) ? null : ms;
}

// The formatted string, or null when this value is not a date this field can hold. `zone` is the reader's
// `userTz`; a calendar day ignores it.
export function formatResultDate(kind, value, { zone } = {}) {
  if (!kind) return null;
  const ms = asMs(value);
  if (ms == null) return null;
  const d = kind === "date" ? dayjs.utc(ms) : inZone(ms, zone || "UTC");
  return kind === "datetime" ? d.format("MMM DD, YYYY HH:mm") : d.format("MMM DD, YYYY");
}
